import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback
} from "react";
import PropTypes from "kf-proptypes/widgets";
import suneditor from "suneditor";
import en from "suneditor/src/lang/en";
import { t } from "@lingui/macro";

import { loadFonts } from "base/utils";
import { FONT_FAMILIES } from "base/constants";
import { i18n } from "base/i18n";

import { EDITOR_PLUGINS } from "./plugins/plugin.list.js";
import { EDITOR_ICONS } from "./icons/icons.list.js";
import { CustomPluginPortals } from "./plugins/custom.portal.jsx";
import {
  COMMANDS,
  COMPONENTS,
  EDITOR_COLOR_LIST,
  EDITOR_FORMATS,
  EDITOR_LINE_HEIGHTS,
  FORMAT_BTN_COMMAND,
  KEYBOARD,
  QUERY_SELECTOR,
  TABLE_CELL_EMPTY_MARKDOWN
} from "./constant.js";
import {
  calculateBounds,
  getTableMenuItems,
  overrideSuneditorMethods
} from "./util.js";
import { ContextMenu } from "./plugins/context_menu/index.jsx";
import { TablePluginPortals } from "./plugins/tableplugin.jsx";

import "./styles/suneditor.override.css";

const DEFAULT_EDITOR_OPTIONS = {
  plugins: EDITOR_PLUGINS,
  icons: EDITOR_ICONS,
  value: "",
  toolbarContainer: "",
  buttonList: [],
  font: [],
  formats: EDITOR_FORMATS,
  tableCellControllerPosition: "bottom",
  showPathLabel: false,
  resizingBar: false,
  defaultStyle: "",
  colorList: EDITOR_COLOR_LIST,
  lineHeights: EDITOR_LINE_HEIGHTS,
  historyStackDelayTime: 100,
  attributesWhitelist: {
    all: "style|data-childtable|src"
  },
  addTagsWhitelist: "img|mark",
  imageUploadUrl: "",
  frameAttrbutes: {
    spellcheck: false
  },
  placeholder: ""
};

function setEditorToolsLang() {
  en.toolbar.orderList = t`Numbered list`;
  en.toolbar.unorderList = t`Bulleted list`;
  en.toolbar.lineHeight = t`Line spacing`;
  en.toolbar.indent = t`Increase indent`;
  en.toolbar.outdent = t`Decrease indent`;
  en.toolbar.removeFormat = t`Clear formatting`;
  en.toolbar.strike = t`Strikethrough`;
  en.toolbar.formats = t`Styles`;
  en.toolbar.fontSize = t`Font size`;
  en.controller.maxSize = t`Fit to width`;
  en.controller.minSize = t`Auto adjust`;
  en.controller.remove = t`Delete`;
  en.controller.splitCells = t`Split cells`;
  en.controller.resize100 = t`Resize to 100%`;
  en.controller.resize75 = t`Resize to 75%`;
  en.controller.resize50 = t`Resize to 50%`;
  en.controller.resize25 = t`Resize to 25%`;
}

export function DocumentEditor(props) {
  const {
    editorOptions,
    customPlugins = [],
    onSave,
    getEditorInstance,
    ...rest
  } = props;
  const editorRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState({
    left: 0,
    top: 0,
    type: ""
  });
  const [activePlugin, setActivePlugin] = useState("");
  const [menuSelection, setMenuSelection] = useState([]);

  useEffect(function loadFontsEfect() {
    // NOTE: below function will be removed, once theme builder is enabled.
    // Because, theme builder will load fonts by default. now loading based on adhoc request
    loadFonts(FONT_FAMILIES);
  }, []);

  useEffect(function InitEditor() {
    setEditorToolsLang();

    let editorPlugins = customPlugins.length
      ? EDITOR_PLUGINS.concat(customPlugins)
      : EDITOR_PLUGINS;

    editorPlugins = editorPlugins.map((pluginItem) => {
      if (pluginItem.title) {
        return {
          ...pluginItem,
          title: i18n._(pluginItem.title),
          innerHTML: i18n._(pluginItem.innerHTML)
        };
      }
      return pluginItem;
    });
    editorInstanceRef.current = suneditor.create(editorRef.current, {
      ...DEFAULT_EDITOR_OPTIONS,
      ...editorOptions,
      plugins: editorPlugins
    });
    /**override suneditor default methods for custom feature */
    const { current: suneditorRef } = editorInstanceRef;
    suneditorRef.core = overrideSuneditorMethods(suneditorRef);
    const core = suneditorRef.core;
    const tablePlugin = core.plugins.table;
    tablePlugin._offCellMultiSelectActual = tablePlugin._offCellMultiSelect;
    tablePlugin._offCellMultiSelect = offCellMultiSelect;
    core.util.custom.setToolbarButtons(COMPONENTS.WYSIWYG);
    const removeEditorEvents = addEditorEventHandlers();
    if (getEditorInstance) getEditorInstance(suneditorRef);
    if (onSave) suneditorRef.onSave = (content) => onSave(content);
    setIsEditorReady(true);

    return function cleanUp() {
      removeEditorEvents();
      suneditorRef.core.plugins.table.customController.removeEventHandlers();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    function initMenuSelection() {
      if (selectedComponent.type === "table") {
        const { core } = editorInstanceRef.current;
        const values = core.plugins.table.getCellValues([
          COMMANDS.BORDER_THICKNESS,
          COMMANDS.BORDER_STROKE,
          COMMANDS.TABLE_BORDER,
          COMMANDS.HEADER,
          COMMANDS.LAYOUT
        ]);
        setMenuSelection(Object.values(values));
        return () => {};
      }
      setMenuSelection([]);
    },
    [selectedComponent.type]
  );

  function getCore() {
    return editorInstanceRef.current.core;
  }

  function addEditorEventHandlers() {
    const core = getCore();
    const { toolbar, wysiwyg } = core.context.element;
    toolbar.addEventListener("click", onClickToolBar);
    wysiwyg.addEventListener("contextmenu", openContextMenu);
    wysiwyg.addEventListener("keyup", onKeyUpWysiwyg);
    wysiwyg.addEventListener("keydown", onKeyDown);
    core._wd.addEventListener("mousedown", onMouseDown);
    return () => {
      toolbar.removeEventListener("click", onClickToolBar);
      wysiwyg.removeEventListener("contextmenu", openContextMenu);
      wysiwyg.removeEventListener("keyup", onKeyUpWysiwyg);
      wysiwyg.removeEventListener("keydown", onKeyDown);
      core._wd.removeEventListener("mousedown", onMouseDown);
    };
  }

  function onKeyUpWysiwyg() {
    const core = getCore();
    const tablePlugin = core.plugins.table;
    const cell = tablePlugin.getSelectedCells();
    if (cell.length === 1 && tablePlugin.isEmptyTableCell(cell[0])) {
      cell[0].innerHTML = TABLE_CELL_EMPTY_MARKDOWN;
    }
  }

  function onKeyDown(e) {
    const core = getCore();
    const key = e.keyCode;
    const tablePlugin = core.plugins.table;
    const { showContextMenu } = tablePlugin.customController;
    showContextMenu && closeContextMenu();
    const table = tablePlugin.getSelectedTable();
    if (table) {
      tablePlugin.customController.offControllers();
      const { SELECTED_CELL } = QUERY_SELECTOR;
      const selectedCells = table.querySelectorAll(`.${SELECTED_CELL}`);
      const isMultiSelect = selectedCells.length > 0;
      switch (key) {
        case KEYBOARD.BACKSPACE:
          if (isMultiSelect) {
            selectedCells.forEach((cell) => {
              cell.innerHTML = TABLE_CELL_EMPTY_MARKDOWN;
            });
            tablePlugin.initTable();
          }
          break;
        case KEYBOARD.CTRL:
          isMultiSelect && (tablePlugin._controlKey = true);
          break;
        default:
          tablePlugin.initTable();
          break;
      }
    }
  }

  function offCellMultiSelect(e) {
    const tablePlugin = this.plugins.table;
    Reflect.apply(tablePlugin._offCellMultiSelectActual, this, [e]);
    const table = tablePlugin._selectedTable;
    const { SELECTED_CELL, ACTIVE_CELL } = QUERY_SELECTOR;
    const query = `.${SELECTED_CELL},.${ACTIVE_CELL}`;
    tablePlugin._selectedCells = table.querySelectorAll(query);
    const activeCells = table.querySelectorAll(`.${ACTIVE_CELL}`);
    activeCells.forEach((cell) => this.util.removeClass(cell, ACTIVE_CELL));
    const selectedCells = table.querySelectorAll(`.${SELECTED_CELL}`);
    this.controllerArray.pop();
    if (selectedCells.length > 0 && !tablePlugin._shift) {
      this.util.custom.setToolbarButtons(COMPONENTS.TABLE_MULTISELECT);
      this.util.custom.removeToolBtnActiveState(FORMAT_BTN_COMMAND);
      if (!tablePlugin._bindOnTableMouseDown) {
        tablePlugin._bindOnTableMouseDown = onTableMouseDown.bind(this);
        table.addEventListener("mousedown", tablePlugin._bindOnTableMouseDown);
      }
    }
  }

  function onTableMouseDown(e) {
    setActivePlugin();
    const tablePlugin = this.plugins.table;
    const selectedCells = tablePlugin.getSelectedCells();
    const selectedCell = Array.from(selectedCells).find((el) =>
      el.contains(e.target)
    );
    if ((e.button === 2 || tablePlugin._controlKey) && selectedCell) {
      e.stopPropagation();
      tablePlugin._controlKey = false;
      tablePlugin.customController.offControllers();
      const bounds = calculateBounds(selectedCell);
      setContextMenuState({
        ...bounds,
        type: "table"
      });
    }
  }

  function onKeyUp() {
    const core = getCore();
    const tablePlugin = core.plugins.table;
    const customUtil = core.util.custom;
    if (tablePlugin._shift) {
      core.controllerArray.pop();
      tablePlugin._shift = false;
      Reflect.apply(tablePlugin._toggleEditor, core, [true]);
      core._wd.removeEventListener("keyup", core._bindCustomKeyUp);
      core._bindCustomKeyUp = null;
      const table = tablePlugin._selectedTable;
      customUtil.setToolbarButtons(COMPONENTS.TABLE_MULTISELECT);
      if (!tablePlugin._bindOnTableMouseDown) {
        tablePlugin._bindOnTableMouseDown = onTableMouseDown.bind(core);
        table.addEventListener("mousedown", tablePlugin._bindOnTableMouseDown);
      }
    }
  }

  function onMouseDown(e) {
    setActivePlugin();
    const core = getCore();
    const tablePlugin = core.plugins.table;
    const customUtil = core.util.custom;
    const btnTray = core.context.element._buttonTray;
    if (!btnTray.contains(e.target)) {
      tablePlugin.initTable();
    }
    if (tablePlugin._shift) {
      if (!core._bindCustomKeyUp) {
        core._bindCustomKeyUp = onKeyUp.bind(this);
        core._wd.addEventListener("keyup", core._bindCustomKeyUp);
      }
    }
    const { wysiwyg } = core.context.element;
    if (wysiwyg.contains(e.target)) {
      const name = customUtil.getComponentName(e);
      customUtil.setToolbarButtons(name);
    }
  }

  function onClickToolBar(e) {
    closeContextMenu();
    const core = getCore();
    const activeMenu = core.submenuActiveButton;
    const command = activeMenu?.dataset.command;
    setActivePlugin(command);
  }

  function openContextMenu(e) {
    const core = getCore();
    const customUtil = core.util.custom;
    const componentName = customUtil.getComponentName(e);
    if (componentName === COMPONENTS.TABLE) {
      e.preventDefault();
      core.plugins.table.customController.showContextMenu = true;
      const bounds = calculateBounds(e.target);
      setContextMenuState({
        ...bounds,
        type: "table"
      });
      core.plugins.table.customController.offControllers();
    }
  }

  function setContextMenuState(state) {
    const { core } = editorInstanceRef.current;
    setSelectedComponent(state);
    core.plugins.table.customController.showContextMenu = !!state.type;
  }

  const closeContextMenu = useCallback(function closeMenu(e) {
    setContextMenuState({ left: 0, top: 0 });
  }, []);

  const getMenuValues = useMemo(
    function getMenuValues() {
      if (selectedComponent.type === "table") {
        const { core } = editorInstanceRef.current;
        return core.plugins.table.getCellValues([
          COMMANDS.BORDER_COLOR,
          COMMANDS.CELL_COLOR,
          COMMANDS.TABLE_BORDER,
          COMMANDS.HEADER
        ]);
      }
      return {};
    },
    [selectedComponent.type]
  );

  const getMenuItems = useMemo(
    function getMenuItems() {
      if (selectedComponent.type === "table") {
        const { core } = editorInstanceRef.current;
        const tablePlugin = core.plugins.table;
        const { _selectedCells: selectedCells } = tablePlugin;
        const isMultiCellSelect = selectedCells.length > 1;
        return getTableMenuItems(isMultiCellSelect);
      }
      return [];
    },
    [selectedComponent.type]
  );

  const updateActivePlugin = useCallback(function updateActivePlugin(name) {
    setActivePlugin(name);
  }, []);

  return (
    <>
      <div ref={editorRef} />
      {isEditorReady && (
        <>
          <CustomPluginPortals
            {...rest}
            ref={editorInstanceRef}
            activePlugin={activePlugin}
            updateActivePlugin={updateActivePlugin}
            documentId={editorOptions.documentId}
          />
          <ContextMenu
            left={selectedComponent.left}
            top={selectedComponent.top}
            show={!!selectedComponent.type}
            onClose={closeContextMenu}
            selection={menuSelection}
            values={getMenuValues}
            items={getMenuItems}
            core={editorInstanceRef.current.core}
            documentId={editorOptions.documentId}
          />
          <TablePluginPortals
            core={editorInstanceRef.current.core}
            documentId={editorOptions.documentId}
            activePlugin={activePlugin}
            updateActivePlugin={updateActivePlugin}
          />
        </>
      )}
    </>
  );
}

DocumentEditor.propTypes = {
  /**
   * The options for the editor like font, colorList, value ...
   */
  editorOptions: PropTypes.shape({
    value: PropTypes.string.isRequired,
    buttonList: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
    toolbarContainer: PropTypes.string,
    font: PropTypes.arrayOf(PropTypes.string),
    formats: PropTypes.arrayOf(PropTypes.string),
    tableCellControllerPosition: PropTypes.string,
    showPathLabel: PropTypes.bool,
    resizingBar: PropTypes.bool,
    style: PropTypes.shape({
      display: PropTypes.string,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    }),
    colorList: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
    historyStackDelayTime: PropTypes.number,
    attributesWhitelist: PropTypes.shape({
      all: PropTypes.string
    }),
    imageUploadUrl: PropTypes.string,
    documentId: PropTypes.string
  }),
  /**
   * An array of custom plugins for the editor.
   */
  customPlugins: PropTypes.arrayOf(PropTypes.element),
  /**
   * A function to be called when the editor's content is saved.
   */
  onSave: PropTypes.func,
  /**
   * A function to be called to get the editor instance.
   */
  getEditorInstance: PropTypes.func
};
